import { api, el, fmt, itemIcon } from './dom.js';
import { renderPackageRewards } from './package-rewards.js';

// Campo "Nome do pacote" com busca: o jogador escolhe um pacote do catálogo da
// Loja de Pérolas (BDO Codex, com ícone e conteúdo) ou um nome já registrado
// pela comunidade, ou simplesmente mantém o que digitou.
export function createPackagePicker(input, { onPick, onAddReward }) {
  const listId = 'mcPackageOptions';
  const list = el('ul', { id: listId, className: 'mc-picker-list', role: 'listbox', hidden: true });
  const preview = el('div', { className: 'mc-picker-preview mc-span-full', hidden: true });
  const rewards = el('div', { className: 'mc-rewards' });
  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('aria-expanded', 'false');
  input.setAttribute('aria-controls', listId);
  input.removeAttribute('list');
  const wrap = el('div', { className: 'mc-picker' });
  input.replaceWith(wrap);
  wrap.append(input, list);
  (wrap.closest('label') || wrap).after(preview);

  let communityNames = [];
  let options = [];
  let active = -1;
  let selected = null;
  let timer = null;
  let requestId = 0;

  function close() {
    list.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    active = -1;
  }

  function highlight(index) {
    active = index;
    [...list.children].forEach((node, position) => node.setAttribute('aria-selected', String(position === index)));
    const node = list.children[index];
    if (node) { input.setAttribute('aria-activedescendant', node.id); node.scrollIntoView({ block: 'nearest' }); }
  }

  function renderPreview() {
    if (!selected) { preview.hidden = true; preview.replaceChildren(); return; }
    const contents = selected.contents || [];
    preview.hidden = false;
    preview.replaceChildren(
      el('div', { className: 'mc-picker-head' }, [
        itemIcon(selected.iconPath, { alt: selected.name }),
        el('div', {}, [
          el('strong', { text: selected.name }),
          el('small', { text: selected.referencePearls ? `Preço de referência no BDO Codex: ${fmt.int(selected.referencePearls)} Pérolas (confira o preço no seu servidor)` : 'Catálogo BDO Codex' })
        ])
      ]),
      contents.length ? el('div', { className: 'mc-picker-contents', 'aria-label': 'Conteúdo segundo o BDO Codex' }, [
        el('small', { text: 'Vem no pacote (BDO Codex):' }),
        ...contents.slice(0, 12).map((item) => el('span', { className: 'mc-chip', title: `Item ${item.sourceItemId}` }, [itemIcon(item.iconPath, { size: 'sm' }), `×${fmt.int(item.quantity)}`]))
      ]) : null,
      rewards
    );
    // Baús e pacotes de sorte: o que pode sair de cada um, carregado à parte.
    if (contents.length) renderPackageRewards(rewards, selected.id, { onAdd: (node) => onAddReward?.(node), referencePearls: selected.referencePearls });
  }

  function choose(option) {
    close();
    input.value = option.name;
    selected = option.catalog ? option.catalog : null;
    renderPreview();
    onPick(selected);
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  function render() {
    list.replaceChildren(...options.map((option, index) => el('li', {
      id: `mcPackageOption${index}`,
      role: 'option',
      className: 'mc-picker-option',
      'aria-selected': 'false',
      onmousedown: (event) => { event.preventDefault(); choose(option); }
    }, [
      itemIcon(option.catalog?.iconPath),
      el('span', {}, [el('strong', { text: option.name }), el('small', { text: option.meta })])
    ])));
    list.hidden = !options.length;
    input.setAttribute('aria-expanded', String(!list.hidden));
    active = -1;
  }

  async function search() {
    const q = input.value.trim();
    if (q.length < 2) { options = []; render(); return; }
    const current = ++requestId;
    const needle = q.toLocaleLowerCase('pt-BR');
    const community = communityNames.filter((name) => name.toLocaleLowerCase('pt-BR').includes(needle)).slice(0, 4)
      .map((name) => ({ name, meta: 'Já registrado pela comunidade' }));
    let catalog = [];
    try {
      catalog = (await api(`/api/tools/cron-calculator/packages?${new URLSearchParams({ q })}`)).items;
    } catch { /* sem catálogo: seguem só os nomes da comunidade */ }
    if (current !== requestId) return;
    const seen = new Set(community.map((option) => option.name.toLocaleLowerCase('pt-BR')));
    options = [
      ...community,
      ...catalog.filter((item) => !seen.has(item.name.toLocaleLowerCase('pt-BR'))).map((item) => ({
        name: item.name,
        catalog: item,
        meta: [item.cronStones ? `${fmt.int(item.cronStones)} Pedras Cron` : null, item.contents?.length ? `${item.contents.length} ${item.contents.length === 1 ? 'item' : 'itens'}` : null, 'BDO Codex'].filter(Boolean).join(' · ')
      }))
    ].slice(0, 12);
    render();
  }

  input.addEventListener('input', (event) => {
    if (!event.isTrusted) return;
    if (selected && input.value.trim() !== selected.name) { selected = null; renderPreview(); onPick(null); }
    clearTimeout(timer);
    timer = setTimeout(search, 200);
  });
  input.addEventListener('keydown', (event) => {
    if (list.hidden) return;
    if (event.key === 'ArrowDown') { event.preventDefault(); highlight(Math.min(active + 1, options.length - 1)); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); highlight(Math.max(active - 1, 0)); }
    else if (event.key === 'Enter' && active >= 0) { event.preventDefault(); choose(options[active]); }
    else if (event.key === 'Escape') close();
  });
  input.addEventListener('blur', () => setTimeout(close, 100));

  return {
    setCommunityNames(names) { communityNames = names; },
    value: () => selected?.id || null,
    selected: () => selected,
    reset() { selected = null; options = []; render(); renderPreview(); },
    // Ao editar um registro que veio do catálogo, mostra o pacote de novo (sem preencher nada).
    async load(catalogPackageId) {
      selected = null;
      if (catalogPackageId) {
        try { selected = await api(`/api/tools/cron-calculator/packages/${encodeURIComponent(catalogPackageId)}`); } catch { selected = null; }
      }
      renderPreview();
    }
  };
}
